/**
 * NL-030 (2026-06-09) — locked-state card shown when a client tries to
 * reach a section that hasn't unlocked yet. Used by:
 *
 *   - The `<StepGate>` route wrapper around /check-in and /carpe-diem.
 *   - The Dashboard's Weekly #1 Focus card before Step 5.
 *
 * Pure presentation: brand-styled bone surface + ink lock glyph +
 * one-line "opens when you start Step N" copy. No interactive
 * controls. DE+EN copy lives under `client.locked.section.*`.
 */
import { useTranslation } from 'react-i18next';

export type LockedSectionKey =
  | 'dailyCheckIn'
  | 'carpeDiem'
  | 'weeklyFocus';

interface StepLockedCardProps {
  readonly sectionKey: LockedSectionKey;
  readonly unlocksAtStep: number;
  /** Set when this card stands in for a full-width route (adds page padding). */
  readonly fullPage?: boolean;
}

export function StepLockedCard({
  sectionKey,
  unlocksAtStep,
  fullPage = false,
}: StepLockedCardProps): JSX.Element {
  const { t } = useTranslation();

  const card = (
    <article
      className="rounded-lg border border-bone-3 bg-bone-2 p-s6 text-center"
      data-testid={`step-locked-${sectionKey}`}
      data-locked-step={unlocksAtStep}
      role="status"
      aria-live="polite"
    >
      <div
        className="mx-auto mb-s4 flex h-12 w-12 items-center justify-center rounded-full bg-bone border border-bone-3 text-2xl text-flame-deep"
        aria-hidden="true"
      >
        🔒
      </div>
      <h2 className="font-display text-display-sm text-ink">
        {t(`client.locked.section.${sectionKey}.title`)}
      </h2>
      <p className="mt-s3 text-gray-2 max-w-prose mx-auto">
        {t(`client.locked.section.${sectionKey}.body`, { step: unlocksAtStep })}
      </p>
    </article>
  );

  if (!fullPage) return card;
  return (
    <section
      className="max-w-3xl mx-auto px-s5 py-s7"
      data-testid={`step-locked-page-${sectionKey}`}
    >
      {card}
    </section>
  );
}
